import { memo } from "react"
import { observer } from "mobx-react-lite"
import { useStore } from "../../../contexts/store"
import { Interaction, InteractionType, iAnnotation } from '../../../shared/src/types/Interaction'
import { Box } from "@mui/material"
import MarginaliaIcon from "./MarginaliaIcon"
import MarginaliaEmoji from "./MarginaliaEmoji"
import MarginaliaNote from "./MarginaliaNote"
//import MarginaliaTag from "./MarginaliaTag"


const Marginalia = (
  {
    pageNumber,
    pdfId,
    podId,
    interactionsShown
  }: {
    pageNumber: number,
    pdfId: string,
    podId: string,
    interactionsShown: InteractionType[]
  }
) => {
  const { podStore, uiStore } = useStore()
  const pdfUrl = `/pod/${podId}/pdf/${pdfId}`
  // width of page margin
  const pageMargin = uiStore.pageMargin
  const radius = uiStore.marginaliaIconRadius

  // get pod object
  const pod = podStore.pod
  if(pod === null) return null

  // list of interactions from which marginalia should be visible
  const marginaliaList: Array<Interaction> = []
  // load interactions for actual page
  const pageInteractions = pod.getInteractionsFromPage(pdfId, pageNumber)
  // filter list of interactions from page
  for(const interaction of pageInteractions) {
    const interactionType = interaction.interactionType
    if(interactionsShown.includes(interactionType)) {
      // do not show marginalia for interactions with no rects
      if(!interaction.anchor.rects.length) continue;
      // highlights have no marginalia
      if(interactionType === 'annotation' && !interaction.label) continue;
      // if interaction goes beyond the page margin: show the corresponding marginalia only once at the beginning
      if(interaction.anchor.rects[0].p !== pageNumber) continue;
      marginaliaList.push(interaction)
    }
  }

  // no marginalia available on this page
  if(marginaliaList.length === 0 || pageMargin === undefined) return null

  // sort order of interactions
  marginaliaList.sort((a: any, b: any) => {
    const aPageNumber = a.anchor.rects[0].p
    const aX = Math.floor(a.anchor.rects[0].x)
    const aY = Math.floor(a.anchor.rects[0].y)

    const bPageNumber = b.anchor.rects[0].p
    const bX = Math.floor(b.anchor.rects[0].x)
    const bY = Math.floor(b.anchor.rects[0].y)

    // compare by page number
    if (aPageNumber > bPageNumber) return 1
    if (aPageNumber < bPageNumber) return -1
    // interaction are on the same page: compare vertical position of the first rectangle
    if(aY > bY) return 1
    if(aY < bY) return -1
    // interactions are on the same height: compare horizontal position of the first rectangle
    if(aX > bX) return 1
    if(aX < bX) return -1
    // interactions share the same position of the first rectangle
    return 0
  })

  // returns appropriate types of marginalia depending on the screen width and interaction type
  const createMarginalia = (item: Interaction) => {
    const deviceWidth = uiStore.deviceWidth
    // show full tag name when sidebar tag is open
    // if(uiStore.deviceWidth === "large" && item.interactionType === 'tagging') {
    //   const height = 28
    //   return {
    //     height: height,
    //     width: pageMargin,
    //     item: (
    //       <MarginaliaTag
    //         height={height}
    //         item={item as iTag}
    //         key={item.interactionId}
    //         pdfUrl={pdfUrl}
    //         width={pageMargin}
    //       />
    //     )
    //   }
    // }
    // show note content on large screens
    if(item.interactionType === "annotation" && (deviceWidth === "medium" || deviceWidth === "large"  || deviceWidth === "xlarge")) {
      const height = 50
      return (
        {
          height: height,
          width: pageMargin,
          item: (
            <MarginaliaNote
              height={height}
              item={item as iAnnotation}
              key={item.interactionId}
              pdfUrl={pdfUrl}
              width={pageMargin}
            />
          )
        }
      )
    }
    // show emotion as icon
    if(item.interactionType === "emotion") return (
      {
        height: radius,
        width: radius,
        item: (
          <MarginaliaEmoji
            radius={radius}
            item={item}
            key={item.interactionId}
            pdfId={pdfId}
            pdfUrl={pdfUrl}
          />
        )
      }
    )
    // show icon for interaction
    else return (
      {
        height: radius,
        width: radius,
        item: (
          <MarginaliaIcon
            radius={radius}
            item={item}
            key={item.interactionId}
            pdfUrl={pdfUrl}
          />
        )
      }
    )
  }

  // create page margin with marginalias
  const Margin = memo(() => {
    // elements which create margin sidebar
    const elements: any = []
    // save marginalia until the next placeholder is added,
    // so that too many interactions can be replaced by an icon.
    const marginaliaMemory: any[] = []
    // status position for gap calculation
    let yPos = 0
    let xPos = 0
    // shifts the marginalia slightly upwards, regardless of their height
    const marginBottom = 10
    // mui iconButton elements create hidden margin on top, if button is too small
    let iconTop = 0
    if ((25 - radius) > 0) {
      iconTop = Math.round((25-radius))
    }
    // for every marginalia item
    // if item y position is larger than the current y status position:
    // fill the gap with an appropriately sized element.
    // otherwise add the element to the margin
    if(pageMargin && marginaliaList.length) {
      marginaliaList.forEach((item: Interaction) => {

        // y position from selected text which refers to marginalia
        const itemY = item.anchor.rects[0].y
        // get marginalia item and its specs
        const createdMarginalia = createMarginalia(item)
        const marginaliaItem = createdMarginalia?.item
        const marginaliaHeight = createdMarginalia?.height
        const marginaliaWidth = createdMarginalia?.width

        if(marginaliaItem && marginaliaHeight && marginaliaWidth) {
          // if y position of item is already reached, add it to the margin
          if(itemY <= (yPos + marginBottom) ) {
            marginaliaMemory.push(marginaliaItem)
            // if page margin starts with marginalia add marginalia height
            if(marginaliaMemory.length === 1) yPos += marginaliaHeight + iconTop
            // when the items become wider than the margin, they move to the next line.
            // which increases the y status by the height of the item
            // and sets the x status to the width of the item
            xPos += marginaliaWidth
            if(xPos >= pageMargin) {
              yPos += marginaliaHeight + iconTop
              xPos = marginaliaWidth
            }
          }
          // if the y position has not yet been reached,
          // draw marginalia included so far
          // place box with the corresponding missing space in front of the next item
          else {
            // draw list of marginalia
            if(marginaliaMemory.length) {
              elements.push(...marginaliaMemory)
              // clear array
              marginaliaMemory.length = 0
            }

            // calculate gap to next item
            const yDiff = itemY - yPos- marginBottom
            // draw box to fill the gap
            elements.push(
              <Box key={`${item.interactionId}Margin`} sx={{width: "100%", height: `${yDiff}px`}} />
            )
            // add marginalia item
            marginaliaMemory.push(marginaliaItem)

            // set yPos to the sum of the added gap and the element itself
            yPos += yDiff + marginaliaHeight + iconTop
            // set xPos to the width of the added item or width of pageMargin if the element occupies the entire width
            xPos = marginaliaWidth

          }
        }
      })

      // add backlog
      elements.push(...marginaliaMemory)
    }
    return elements
  })


  return (
    <Box sx={{wordBreak: "break-word", userSelect:'none'}}>
      <Margin />
    </Box>
  )

}

export default observer(Marginalia)